/**
 * Export utilities for visual components
 * Handles PNG, SVG, and embed code generation
 */

import { FONT_TEXT, FONT_DISPLAY } from '@/app/fonts';

/**
 * Copy the site's next/font @font-face rules into an SVG as data URIs.
 * A standalone SVG document (serialized, then rasterized through an <img>)
 * can neither resolve var() nor fetch external URLs, so the woff2 bytes must
 * travel inside the SVG itself for the exported PNG to use the brand fonts.
 */
async function embedSiteFonts(svg: SVGSVGElement): Promise<void> {
  const wanted = new Set([FONT_TEXT, FONT_DISPLAY]);
  const fontUrls = new Map<string, string>();

  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // cross-origin sheet
    }
    for (const rule of Array.from(rules)) {
      if (!(rule instanceof CSSFontFaceRule)) continue;
      const family = rule.style.getPropertyValue('font-family').replace(/["']/g, '').trim();
      if (!wanted.has(family) || fontUrls.has(family)) continue;
      const src = rule.style.getPropertyValue('src');
      const match = src.match(/url\(["']?([^"')]+)["']?\)/);
      if (match) fontUrls.set(family, match[1]);
    }
  }

  if (fontUrls.size === 0) return;

  const styleText = await Promise.all(
    Array.from(fontUrls.entries()).map(async ([family, url]) => {
      const blob = await fetch(url).then((res) => {
        if (!res.ok) throw new Error(`Font fetch failed: ${url}`);
        return res.blob();
      });
      const dataUri = await blobToDataURL(blob);
      return `@font-face { font-family: '${family}'; src: url(${dataUri}) format('woff2'); }`;
    })
  ).then((lines) => lines.join('\n'));

  let defs = svg.querySelector('defs');
  if (!defs) {
    defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    svg.insertBefore(defs, svg.firstChild);
  }
  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
  style.textContent = styleText;
  defs.appendChild(style);
}

// Helper function to convert blob to data URL
async function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Helper function to load external images and convert to data URLs
async function inlineExternalImages(svgElement: SVGSVGElement): Promise<SVGSVGElement> {
  const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;
  const images = clonedSvg.querySelectorAll('image');
  
  for (const img of Array.from(images)) {
    const href = img.getAttribute('xlink:href') || img.getAttribute('href');
    if (href && (href.startsWith('/') || href.startsWith('http'))) {
      try {
        // Build full URL for relative paths
        const fullUrl = href.startsWith('http') 
          ? href 
          : `${window.location.origin}${href}`;
        
        const response = await fetch(fullUrl);
        if (response.ok) {
          const blob = await response.blob();
          const dataUrl = await blobToDataURL(blob);
          img.setAttribute('href', dataUrl);
          img.removeAttribute('xlink:href');
        }
      } catch (error) {
        console.warn(`Could not inline image: ${href}`, error);
      }
    }
  }
  
  return clonedSvg;
}

export async function downloadAsPNG(
  svgElement: SVGSVGElement, 
  filename: string,
  onProgress?: (progress: number, message: string) => void
) {
  try {
    const startTime = Date.now();
    
    // Step 1: Load fonts (33% progress)
    if (onProgress) onProgress(10, 'Loading fonts...');
    
    // Ensure the site fonts are loaded before rasterizing
    try {
      await Promise.all([
        document.fonts.load(`400 16px ${FONT_TEXT}`),
        document.fonts.load(`700 16px ${FONT_TEXT}`),
        document.fonts.load(`700 16px ${FONT_DISPLAY}`)
      ]);
    } catch (fontError) {
      console.warn('Font loading failed, continuing anyway:', fontError);
    }
    
    if (onProgress) onProgress(33, 'Loading fonts...');
    
    // Step 2: Inline external images (66% progress)
    if (onProgress) onProgress(40, 'Fetching assets...');
    
    const processedSvg = await inlineExternalImages(svgElement);

    // Embed the site fonts as data URIs so the standalone SVG (and the PNG
    // rasterized from it) renders in the real typefaces — an <img>-embedded
    // SVG cannot load external fonts, and var() does not resolve there.
    await embedSiteFonts(processedSvg);

    // Apply font-family directly to all text elements
    const textElements = processedSvg.querySelectorAll('text, tspan');
    textElements.forEach((element) => {
      element.setAttribute('font-family', `${FONT_TEXT}, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif`);
    });
    
    if (onProgress) onProgress(66, 'Preparing image...');
    
    // Step 3: Convert to PNG (100% progress)
    const svgData = new XMLSerializer().serializeToString(processedSvg);
    
    // Create a canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');
    
    // Get dimensions from viewBox or default
    const viewBox = svgElement.viewBox.baseVal;
    const width = viewBox.width || 700;
    const height = viewBox.height || 480;
    
    // Set canvas size (2x for retina)
    canvas.width = width * 2;
    canvas.height = height * 2;
    ctx.scale(2, 2);
    
    // Create an image
    const img = new Image();
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    // Ensure minimum time for smooth animation (3 seconds total)
    const elapsedTime = Date.now() - startTime;
    const remainingTime = Math.max(0, 2800 - elapsedTime);
    
    if (remainingTime > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingTime));
    }
    
    if (onProgress) onProgress(90, 'Generating PNG...');
    
    return new Promise<void>((resolve, reject) => {
      img.onload = () => {
        // Draw the image
        ctx.drawImage(img, 0, 0, width, height);
        
        if (onProgress) onProgress(100, 'Download ready!');
        
        // Small delay to show completion
        setTimeout(() => {
          // Convert to blob and download
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Could not create PNG blob'));
              return;
            }
            
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${filename}.png`;
            link.href = downloadUrl;
            link.click();
            
            // Cleanup
            URL.revokeObjectURL(url);
            URL.revokeObjectURL(downloadUrl);
            resolve();
          }, 'image/png');
        }, 200);
      };
      
      img.onerror = () => {
        reject(new Error('Could not load SVG image'));
      };
      
      img.src = url;
    });
  } catch (error) {
    console.error('Error downloading PNG:', error);
    throw error;
  }
}

export async function downloadAsSVG(svgElement: SVGSVGElement, filename: string) {
  try {
    // Clone the SVG to avoid modifying the original
    const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;
    
    // Add XML declaration and DOCTYPE
    const svgData = new XMLSerializer().serializeToString(clonedSvg);
    const fullSvg = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">
${svgData}`;
    
    // Create blob and download
    const blob = new Blob([fullSvg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.download = `${filename}.svg`;
    link.href = url;
    link.click();
    
    // Cleanup
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading SVG:', error);
    throw error;
  }
}

export function copyToClipboard(text: string) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).then(
      () => {
        // Show success toast or feedback
        console.log('Copied to clipboard');
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  } else {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      console.log('Copied to clipboard (fallback)');
    } catch (err) {
      console.error('Could not copy text: ', err);
    }
    
    document.body.removeChild(textArea);
  }
}

export function generateEmbedCode(name: string, width: number, height: number, title: string): string {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://leadpops.com';
  return `<iframe 
  src="${baseUrl}/embed/${name}" 
  width="${width}" 
  height="${height}" 
  frameborder="0" 
  title="${title}"
  loading="lazy"
  style="border: 1px solid #e5e7eb; border-radius: 8px;">
</iframe>`;
}

export function generateShareUrl(name: string): string {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://leadpops.com';
  return `${baseUrl}/visuals/${name}`;
}

export function generateSocialShareLinks(name: string, title: string, description: string) {
  const url = generateShareUrl(name);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);
  
  return {
    twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&title=${encodedTitle}&summary=${encodedDescription}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`
  };
}